import React, { useState, useEffect } from 'react';
import virtualTransactionService from '../services/virtualTransactionService';
import accountService from '../services/accountService';
import categoryService from '../services/categoryService';
import { formatError } from '../utils/errorHelper';
import { getIconComponent } from '../utils/iconHelper';
import { 
  MdEdit, 
  MdDelete, 
  MdToggleOn, 
  MdToggleOff, 
  MdCalendarToday,
  MdDescription,
  MdArrowUpward,
  MdArrowDownward,
  MdViewList,
  MdCalendarViewMonth
} from 'react-icons/md';
import { format } from 'date-fns';
import CategorySelect from '../components/CategorySelect';
import AccountSelect from '../components/AccountSelect';
import CurrencyInput from '../components/CurrencyInput';
import MonthPicker from '../components/MonthPicker';
import ConfirmDialog from '../components/ConfirmDialog';
import TransactionCalendar from '../components/TransactionCalendar';
import './VirtualTransactions.css';

function VirtualTransactions() {
  const [virtualTransactions, setVirtualTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' ou 'calendar'
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    day_of_month: '1',
    account_id: '',
    category_id: '',
    type: 'expense',
    is_active: true,
    start_date: format(new Date(), 'yyyy-MM-dd'),
  });
  const [errors, setErrors] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'warning',
    confirmText: 'Confirmar',
    cancelText: 'Cancelar'
  });

  useEffect(() => {
    loadData();
  }, []);

  // Função para adicionar notificação
  const addNotification = (type, title, message, duration = 5000) => {
    const id = Date.now();
    const notification = { id, type, title, message };
    
    setNotifications(prev => [...prev, notification]);
    
    // Auto-remover após duration
    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }
  };

  // Função para remover notificação
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [vTransactionsData, accountsData, categoriesData] = await Promise.all([
        virtualTransactionService.getVirtualTransactions(),
        accountService.getAccounts(),
        categoryService.getCategories(),
      ]);

      setVirtualTransactions(vTransactionsData.data || vTransactionsData);
      setAccounts(accountsData.data || accountsData);
      setCategories(categoriesData.data || categoriesData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      addNotification('error', 'Erro ao Carregar', 'Erro ao carregar dados. Verifique sua conexão.', 6000);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    
    try {
      const dataToSend = {
        ...formData,
        amount: parseFloat(formData.amount),
        day_of_month: parseInt(formData.day_of_month),
      };

      if (editingTransaction) {
        await virtualTransactionService.updateVirtualTransaction(
          editingTransaction.id,
          dataToSend
        );
      } else {
        await virtualTransactionService.createVirtualTransaction(dataToSend);
      }

      addNotification('success', 'Sucesso', editingTransaction ? 'Transação fixa atualizada com sucesso!' : 'Transação fixa criada com sucesso!', 3000);
      setShowForm(false);
      setEditingTransaction(null);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Erro ao salvar transação fixa:', error);
      
      // Extrai erros de validação do backend
      if (error?.response?.data?.errors) {
        const backendErrors = {};
        Object.keys(error.response.data.errors).forEach((field) => {
          const fieldErrors = error.response.data.errors[field];
          // Pega a primeira mensagem de erro de cada campo
          backendErrors[field] = Array.isArray(fieldErrors) ? fieldErrors[0] : fieldErrors;
        });
        setErrors(backendErrors);
      } else {
        // Se não houver erros específicos, mostra uma notificação geral
        addNotification('error', 'Erro ao Salvar', formatError(error, 'Erro ao salvar transação fixa. Verifique os dados.'), 6000);
      }
    }
  };

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setFormData({
      description: transaction.description,
      amount: transaction.amount.toString(),
      day_of_month: transaction.day_of_month.toString(),
      account_id: transaction.account_id,
      category_id: transaction.category_id,
      type: transaction.type,
      is_active: transaction.is_active,
      start_date: transaction.start_date || format(new Date(), 'yyyy-MM-dd'),
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Excluir Transação Fixa',
      message: 'Deseja realmente excluir esta transação fixa?\n\nTodas as transações pendentes geradas por ela também serão removidas.\n\nEsta ação não pode ser desfeita.',
      type: 'error',
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        try {
          await virtualTransactionService.deleteVirtualTransaction(id);
          addNotification('success', 'Sucesso', 'Transação fixa excluída com sucesso!', 3000);
          loadData();
        } catch (error) {
          console.error('Erro ao excluir transação fixa:', error);
          addNotification('error', 'Erro ao Excluir', 'Erro ao excluir transação fixa. Tente novamente.', 5000);
        }
      }
    });
  };

  const toggleActive = async (id) => {
    try {
      await virtualTransactionService.toggleActive(id);
      loadData();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      description: '',
      amount: '',
      day_of_month: '1',
      account_id: '',
      category_id: '',
      type: 'expense',
      is_active: true,
      start_date: format(new Date(), 'yyyy-MM-dd'),
    });
    setErrors({});
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getCategory = (id) => {
    return categories.find((c) => c.id === id);
  };

  const getAccount = (id) => {
    return accounts.find((a) => a.id === id);
  };

  const getDayLabel = (day) => {
    if (day === 31) return 'Último dia';
    if (day === 1) return '1º dia';
    return `Dia ${day}`;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Carregando...</p>
      </div>
    );
  }

  return (
    <div className="virtual-transactions-page">
      <div className="page-header">
        <div>
          <h1>Transações Fixas</h1>
          <p className="page-subtitle">Gerencie suas receitas e despesas recorrentes</p>
        </div>
        <div className="page-header-actions">
          <div className="view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="Visualização em lista"
            >
              <MdViewList size={20} />
              <span>Lista</span>
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'calendar' ? 'active' : ''}`}
              onClick={() => setViewMode('calendar')}
              title="Visualização em calendário"
            >
              <MdCalendarViewMonth size={20} />
              <span>Calendário</span>
            </button>
          </div>
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            + Nova Transação Fixa
          </button>
        </div>
      </div>

      {showForm && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingTransaction ? 'Editar' : 'Nova'} Transação Fixa</h2>
              <button
                className="close-btn"
                onClick={() => {
                  setShowForm(false);
                  setEditingTransaction(null);
                  resetForm();
                }}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit} className="transaction-form virtual-transaction-form">
              <div className="virtual-transaction-form-body">
                <div className="form-type-selector">
                  <button
                    type="button"
                    className={`type-btn ${formData.type === 'income' ? 'active income' : ''}`}
                    onClick={() => {
                      setFormData({ ...formData, type: 'income', category_id: '' });
                    }}
                  >
                    <MdArrowUpward size={24} />
                    <span>Receita</span>
                  </button>
                  <button
                    type="button"
                    className={`type-btn ${formData.type === 'expense' ? 'active expense' : ''}`}
                    onClick={() => {
                      setFormData({ ...formData, type: 'expense', category_id: '' });
                    }}
                  >
                    <MdArrowDownward size={24} />
                    <span>Despesa</span>
                  </button>
                </div>

                <div className="form-group with-icon">
                  <label>Descrição</label>
                  <div className="input-wrapper">
                    <MdDescription className="input-icon" size={20} />
                    <input
                      type="text"
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                      required
                      placeholder="Ex: Netflix, Aluguel, Salário..."
                      className={errors.description ? 'error' : ''}
                    />
                  </div>
                  {errors.description && (
                    <span className="error-message">{errors.description}</span>
                  )}
                </div>

                <div className="form-group">
                  <CurrencyInput
                    value={formData.amount}
                    onChange={(amount) => setFormData({ ...formData, amount })}
                    label="Valor"
                    required
                    hasError={!!errors.amount}
                  />
                  {errors.amount && (
                    <span className="error-message">{errors.amount}</span>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group with-icon">
                    <label>Dia do Mês</label>
                    <div className="input-wrapper">
                      <MdCalendarToday className="input-icon" size={20} />
                      <select
                        name="day_of_month"
                        value={formData.day_of_month}
                        onChange={handleFormChange}
                        required
                        className={errors.day_of_month ? 'error' : ''}
                      >
                        {[...Array(31)].map((_, i) => {
                          const day = i + 1;
                          return (
                            <option key={day} value={day}>
                              {getDayLabel(day)}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    {errors.day_of_month && (
                      <span className="error-message">{errors.day_of_month}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <MonthPicker
                      value={formData.start_date}
                      onChange={(date) => setFormData({ ...formData, start_date: date })}
                      label="Mês de Início"
                      required
                      hasError={!!errors.start_date}
                    />
                    {errors.start_date && (
                      <span className="error-message">{errors.start_date}</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <AccountSelect
                    accounts={accounts}
                    selectedId={formData.account_id}
                    onSelect={(accountId) => setFormData({ ...formData, account_id: accountId })}
                    label="Conta"
                    hasError={!!errors.account_id}
                  />
                  {errors.account_id && (
                    <span className="error-message">{errors.account_id}</span>
                  )}
                </div>

                <div className="form-group">
                  <CategorySelect
                    categories={categories}
                    selectedId={formData.category_id}
                    onSelect={(categoryId) => setFormData({ ...formData, category_id: categoryId })}
                    type={formData.type}
                    label="Categoria"
                    hasError={!!errors.category_id}
                  />
                  {errors.category_id && (
                    <span className="error-message">{errors.category_id}</span>
                  )}
                </div>

                <div className="form-group-checkbox">
                  <input
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleFormChange}
                  />
                  <label htmlFor="is_active">Ativa (aparecerá nos meses futuros)</label>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowForm(false);
                    setEditingTransaction(null);
                    resetForm();
                  }}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {editingTransaction ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {virtualTransactions.length === 0 ? (
        <div className="empty-state">
          <MdCalendarToday size={80} color="#ccc" />
          <h2>Nenhuma transação fixa cadastrada</h2>
          <p>
            Transações fixas são receitas ou despesas que se repetem todo mês,
            como salário, aluguel, contas de serviços, assinaturas, etc.
          </p>
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            Cadastrar Primeira Transação Fixa
          </button>
        </div>
      ) : (
        <>
          {viewMode === 'calendar' ? (
            <TransactionCalendar
              virtualTransactions={virtualTransactions}
              categories={categories}
              accounts={accounts}
              getCategory={getCategory}
              getAccount={getAccount}
              formatCurrency={formatCurrency}
            />
          ) : (
            <div className="virtual-transactions-list">
              {virtualTransactions.map((transaction) => {
                const category = getCategory(transaction.category_id);
                const account = getAccount(transaction.account_id);
                const CategoryIcon = category ? getIconComponent(category.icon) : null;

                return (
                  <div
                    key={transaction.id}
                    className={`virtual-transaction-item ${transaction.type} ${
                      !transaction.is_active ? 'inactive' : ''
                    }`}
                  >
                    <div className="transaction-icon-wrapper">
                      <div
                        className="transaction-icon"
                        style={{ backgroundColor: category?.color || '#999' }}
                      >
                        {CategoryIcon && <CategoryIcon size={24} color="white" />}
                      </div>
                    </div>

                    <div className="transaction-content">
                      <div className="transaction-header">
                        <h3 className="transaction-description">{transaction.description}</h3>
                        <span className={`transaction-amount ${transaction.type}`}>
                          {transaction.type === 'income' ? '+' : '-'}
                          {formatCurrency(Math.abs(transaction.amount))}
                        </span>
                      </div>

                      <div className="transaction-meta">
                        <div className="transaction-day">
                          <MdCalendarToday size={16} />
                          <span>{getDayLabel(transaction.day_of_month)} do mês</span>
                        </div>
                        {transaction.start_date && (
                          <div className="transaction-start-date">
                            <span className="meta-label">Desde:</span>
                            <span className="meta-value">
                              {(() => {
                                try {
                                  // Tenta fazer parse da data
                                  const dateStr = transaction.start_date.includes('T') 
                                    ? transaction.start_date 
                                    : transaction.start_date + 'T00:00:00';
                                  const date = new Date(dateStr);
                                  
                                  // Verifica se a data é válida
                                  if (isNaN(date.getTime())) {
                                    return '-';
                                  }
                                  
                                  const month = String(date.getMonth() + 1).padStart(2, '0');
                                  const year = date.getFullYear();
                                  return `${month}/${year}`;
                                } catch (error) {
                                  console.error('Erro ao formatar data:', error);
                                  return '-';
                                }
                              })()}
                            </span>
                          </div>
                        )}
                        <div className="transaction-category">
                          <span className="meta-label">Categoria:</span>
                          <span className="meta-value">{category?.name || '-'}</span>
                        </div>
                        <div className="transaction-account">
                          <span className="meta-label">Conta:</span>
                          <span className="meta-value">{account?.name || '-'}</span>
                        </div>
                      </div>

                      <div className="transaction-footer">
                        <div className="transaction-status">
                          {transaction.is_active ? (
                            <span className="status-badge active">
                              <MdToggleOn size={20} />
                              Ativa
                            </span>
                          ) : (
                            <span className="status-badge inactive-badge">
                              <MdToggleOff size={20} />
                              Inativa
                            </span>
                          )}
                        </div>

                        <div className="transaction-actions">
                          <button
                            className="action-btn status-toggle"
                            onClick={() => toggleActive(transaction.id)}
                            title={transaction.is_active ? 'Desativar' : 'Ativar'}
                          >
                            {transaction.is_active ? (
                              <MdToggleOff size={20} />
                            ) : (
                              <MdToggleOn size={20} />
                            )}
                          </button>
                          <button
                            className="action-btn edit"
                            onClick={() => handleEdit(transaction)}
                            title="Editar"
                          >
                            <MdEdit size={20} />
                          </button>
                          <button
                            className="action-btn delete"
                            onClick={() => handleDelete(transaction.id)}
                            title="Excluir"
                          >
                            <MdDelete size={20} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Toast Notifications */}
      <div className="toast-container">
        {notifications.map((notification) => (
          <div key={notification.id} className={`toast ${notification.type}`}>
            <div className="toast-icon">
              {notification.type === 'success' && '✓'}
              {notification.type === 'error' && '✕'}
              {notification.type === 'warning' && '⚠'}
              {notification.type === 'info' && 'ℹ'}
            </div>
            <div className="toast-content">
              <h4 className="toast-title">{notification.title}</h4>
              <p className="toast-message">{notification.message}</p>
            </div>
            <button 
              className="toast-close" 
              onClick={() => removeNotification(notification.id)}
              aria-label="Fechar"
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
      />
    </div>
  );
}

export default VirtualTransactions;
